import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ArrowLeftCircle, Plus } from "lucide-react";
import { db } from "./firebase/firebaseConfig";

const Container = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(0, 212, 255, 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgba(255, 122, 24, 0.1), transparent 26%),
    #0a0d12;
  color: #eef4ff;
  padding: 38px;

  * {
    box-sizing: border-box;
  }

  @media (max-width: 640px) {
    padding: 22px 14px;
  }
`;

const Header = styled.div`
  max-width: 920px;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #f8fbff;
  font-size: 1.85rem;
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: #a8b3c7;
  font-weight: 500;
`;

const Form = styled.form`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  display: grid;
  gap: 16px;
  background: rgba(12, 18, 28, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 22px;
  box-shadow: 0 24px 45px rgba(0, 0, 0, 0.18);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  min-width: 0;
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  min-width: 0;
  color: #a8b3c7;
  font-weight: 700;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 150px;
  resize: vertical;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;
  line-height: 1.5;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 520px) {
    > button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;
  color: #071018;
  background: linear-gradient(135deg, #00d4ff, #7dd3fc);
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
`;

const BackButton = styled(Button)`
  color: #d7dee9;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const AddFAQ = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    category: "",
    sortOrder: "",
    status: "Active",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) {
      alert("Question and answer are required");
      return;
    }

    try {
      setSaving(true);
      await addDoc(collection(db, "faqs"), {
        question: form.question.trim(),
        answer: form.answer.trim(),
        category: form.category.trim(),
        sortOrder: Number(form.sortOrder || 0),
        status: form.status,
        createdAt: serverTimestamp(),
      });
      alert("FAQ added");
      navigate("/faqs");
    } catch (err) {
      console.error(err);
      alert("Error adding FAQ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>Add FAQ</Title>
          <Subtitle>Create a frequently asked question for the website.</Subtitle>
        </div>
        <BackButton type="button" onClick={() => navigate("/faqs")}>
          <ArrowLeftCircle size={18} /> Back
        </BackButton>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Grid>
          <Field>
            Category
            <Input name="category" placeholder="General / Pricing / Courses" value={form.category} onChange={handleChange} />
          </Field>
          <Field>
            Sort Order
            <Input type="number" name="sortOrder" placeholder="1" value={form.sortOrder} onChange={handleChange} />
          </Field>
          <Field>
            Status
            <Select name="status" value={form.status} onChange={handleChange}>
              <option>Active</option>
              <option>Hidden</option>
            </Select>
          </Field>
        </Grid>

        <Field>
          Question
          <Input name="question" placeholder="What services do you provide?" value={form.question} onChange={handleChange} />
        </Field>
        <Field>
          Answer
          <Textarea name="answer" placeholder="Write the answer..." value={form.answer} onChange={handleChange} />
        </Field>

        <ButtonRow>
          <Button type="submit" disabled={saving}>
            <Plus size={18} /> {saving ? "Saving..." : "Add FAQ"}
          </Button>
          <BackButton type="button" onClick={() => navigate("/faqs")}>
            Cancel
          </BackButton>
        </ButtonRow>
      </Form>
    </Container>
  );
};

export default AddFAQ;
